<template>
  <page :title="pageTitle" backURI="/billing/accounts" :loading="loading">
    <template #controls>
      <div class="billing-accounts-editor-controls">
        <div class="billing-page-search-controls">
          <form-button v-show="!pageSearchOpen" class="billing-page-search-toggle"
                       :aria-expanded="pageSearchOpen"
                       @click="togglePageSearch">
            <i class="fal fa-search"></i>
          </form-button>
          <div class="billing-page-search-expand" :class="{ 'is-open': pageSearchOpen }"
               :aria-hidden="!pageSearchOpen">
            <div class="billing-page-search-expand-inner">
              <div class="billing-page-search-field-wrap">
                <text-input ref="pageSearchInput" class="billing-page-search-input"
                            v-model="pageSearchQuery" placeholder="Type to find…"
                            @keydown.native="onPageSearchKeydown" />
                <button type="button" class="billing-page-search-close" aria-label="Close search"
                        @click="closePageSearch">
                  <i class="fal fa-times"></i>
                </button>
              </div>
              <div v-if="pageSearchQuery.trim() && pageSearchMatchCount > 0" class="billing-page-search-meta">
                <span>{{ pageSearchMatchIndex + 1 }} / {{ pageSearchMatchCount }}</span>
                <button type="button" class="billing-page-search-nav" title="Previous (Shift+Enter)"
                        @click="pageSearchStep(-1)">
                  <i class="fal fa-chevron-up"></i>
                </button>
                <button type="button" class="billing-page-search-nav" title="Next (Enter)"
                        @click="pageSearchStep(1)">
                  <i class="fal fa-chevron-down"></i>
                </button>
              </div>
              <div v-else-if="pageSearchQuery.trim() && pageSearchMatchCount === 0"
                   class="billing-page-search-meta">
                No matches
              </div>
            </div>
          </div>
        </div>
        <form-button
          v-if="isEditing && $checkPermission('billing.ui.report')"
          icon="fal fa-file-invoice"
          @click="goToReport"
        >Report</form-button>
        <form-button v-if="isEditing && $checkPermission('billing.accounts.delete')" icon="fal fa-trash"
                     @click="confirmDeletion">Delete Billing Account</form-button>
      </div>
    </template>

    <template #body>
      <div ref="billingPageSearchRoot" class="billing-page-search-root">
        <div class="form">
          <div class="input-group size-md">
            <dropdown v-model="account.account" :list="accountList"
                      :loading="loadingAccountList" :options="{ searchMode: 'customOnType' }" label="Account"
                      @search="searchAccount" @listClosed="accountList = []" disabled />
          </div>

          <div class="input-group size-md">
            <text-input label="System" :value="systemName" disabled />
          </div>

          <div class="input-group size-md">
            <dropdown v-model="account.currency" :list="currencyList" label="Currency" />
          </div>

          <div class="input-group">
            <div class="label">Data Plan</div>
            <div class="data-plans-container">
              <div class="data-plan-section" :class="{ 'section-disabled': billableSectionDisabled }">
                <div class="data-plan-section-title first">
                  Billable
                  <button v-if="account.rates.normal.length > 1" class="sort-btn" :class="{ active: rateSortMode }"
                          :title="rateSortMode ? 'Expand all bills' : 'Collapse all bills'"
                          @click="toggleRateSortMode">
                    <i class="fas fa-sort-alt"></i>
                  </button>
                </div>
                <draggable v-model="account.rates.normal" draggable=".data-plan-item" handle=".grip" animation="500"
                           class="data-plans-list" :class="{ 'sort-mode': rateSortMode }">
                  <transition-group name="show-down">
                    <div v-for="rate in account.rates.normal" :key="rate.id" class="data-plan-item"
                         :data-rate-id="rate.id"
                         :class="{ 'is-collapsed': isRateCollapsed(rate.id) }">
                      <div class="data-plan-actions">
                        <button v-if="!rateSortMode || isRateIndividuallyExpanded(rate.id)"
                                class="icon-btn remove-rate-btn"
                                @click="removeBillableRate(rate.id)">
                          <i class="fal fa-minus-circle"></i>
                        </button>

                        <button v-if="rateSortMode && !isRateIndividuallyExpanded(rate.id)" class="icon-btn grip">
                          <i class="fal fa-grip-lines"></i>
                        </button>
                      </div>
                      <div>
                        <button v-if="rateSortMode" class="collapse-bill-btn"
                                :title="isRateIndividuallyExpanded(rate.id) ? 'Collapse this bill' : 'Expand this bill'"
                                @click="toggleRateExpansion(rate.id)">
                          <i class="fas" :class="isRateIndividuallyExpanded(rate.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>

                        <div class="input-group">
                          <text-input label="Description" v-model="rate.description" />
                        </div>

                        <div class="input-group size-md">
                          <dropdown label="Part Number" v-model="rate.partNumber" :list="partNumberList"
                                    :options="{ searchMode: 'none' }"  />
                        </div>

                        <div class="input-group size-sm">
                          <text-input label="Rate" :suffix="account.currency.value" type="number" class="rate"
                                      v-model.number="rate.rate" />
                        </div>

                        <div class="billing-cycle-group rate-billing-cycle"
                             :class="{ active: rate.cycle.cycle.value === 'prepaid' }">
                          <div class="input-group size-sm size-sm-full">
                            <dropdown v-model="rate.cycle.cycle" :list="billingCycleList"
                                      :options="{ searchMode: 'none' }" label="Billing Cycle" />
                          </div>

                          <template v-if="rate.cycle.cycle.value === 'prepaid'">
                            <div class="input-group size-sm">
                              <text-input label="Period" suffix="months" type="number"
                                          v-model.number="rate.cycle.period" />
                            </div>

                            <div class="input-group size-sm">
                              <text-input label="Effect date" type="date" v-model="rate.cycle.effectDate" />
                            </div>

                            <div class="input-group">
                              <checkbox label="Auto Renew" v-model="rate.cycle.autoRenew" />
                            </div>

                            <div class="input-group" v-if="!rate.cycle.autoRenew">
                              <dropdown v-model="rate.cycle.onExpires" :list="expirationActionsList"
                                        :options="{ searchMode: 'none' }" label="On Expiration" />
                            </div>
                          </template>
                        </div>

                        <div class="data-plan-targets-container">
                          <div class="data-plan-targets-title">Targets</div>
                          <div class="data-plan-target-item">
                            <input-list label="Vehicle Batch Settings"
                                        :list="rate.cycle.cycle.value === 'prepaid' ? vehicleBatchSettings : computedVehicleBatchSettings"
                                        :chip-area-max-height="220"
                                        v-model="rate.targets.vehicleTypes" />
                            <input-list label=" Vehicle Definitions"
                                        :list="rate.cycle.cycle.value === 'prepaid' ? vehicleGraphicList : computedVehicleGraphicList"
                                        :chip-area-max-height="220"
                                        v-model="rate.targets.vehicleDefinitions" />
                            <div class="devices-row">
                              <input-list label=" Devices"
                                          :list="computedDeviceList"
                                          :dim-values="pausedDeviceIds"
                                          :chip-area-max-height="220"
                                          v-model="rate.targets.devices" />
                              <button class="icon-btn edit-classifications-btn"
                                      type="button"
                                      title="Manage paused devices"
                                      @click="openPauseModal(rate)">
                                <i class="fal fa-pen"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      <!-- <transition name=" show-down"> -->
                      <!-- <checkbox v-if="account.rates.normal.length === 1" label="All devices" class="all-devices"
                                  v-model="rate.allTargets" :disabled="billableSectionDisabled" @input="clearAllDeviceLists" />
                      </transition> -->
                      </div>
                    </div>
                  </transition-group>
                </draggable>

                <div v-if="account.rates.normal.length === 0" class="no-items">No Billable items added yet</div>

                <div v-if="!rateSortMode" class="data-plan-footer">
                  <button class="icon-btn" @click="addBillableRate" :disabled="!account.account">
                    <i class="fal fa-plus-circle"></i>
                  </button>
                </div>
              </div>

              <div class="data-plan-section" :class="{ 'section-disabled': customerSatisfactionSectionDisabled }">
                <div class="data-plan-section-title">Customer Satisfaction</div>
                <div class="data-plans-list">
                  <div class="data-plan-item">
                    <div>
                      <div class="data-plan-targets-container">
                        <div class="data-plan-target-item">
                          <input-list label="Devices" :list="computedDeviceList"
                                      v-if="!account.rates.customerSatisfaction[0].allTargets"
                                      :chip-area-max-height="220"
                                      v-model="account.rates.customerSatisfaction[0].targets.devices"
                                      :disabled="customerSatisfactionInputsDisabled" />
                        </div>
                      </div>
                      <div></div>
                      <div class="input-group-checkboxes">
                        <div class="all-devices">
                          <checkbox @input="clearAllDeviceLists"
                                    v-model="account.rates.customerSatisfaction[0].allTargets" label="All devices"
                                    :disabled="customerSatisfactionInputsDisabled" />
                        </div>
                        <div class="expires-container">
                          <checkbox label="Expires" v-model="account.rates.customerSatisfaction[0].cycle.expires"
                                    :disabled="customerSatisfactionInputsDisabled" />
                        </div>
                      </div>

                      <div v-if="account.rates.customerSatisfaction[0].cycle.expires" class="expiration-options">
                        <div class="input-group size-sm-md">
                          <text-input label="Expires on" type="month"
                                      v-model="account.rates.customerSatisfaction[0].cycle.dueDate" />
                        </div>
                        <div class="input-group">
                          <dropdown v-model="account.rates.customerSatisfaction[0].cycle.onExpires"
                                    :list="expirationActionsList" :options="{ searchMode: 'none' }" label="On Expiration" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="data-plan-section" :class="{ 'section-disabled': researchAndDevelopmentSectionDisabled }">
                <div class="data-plan-section-title">Research & Development</div>
                <div class="data-plans-list">
                  <div class="data-plan-item last">
                    <div>
                      <div class="data-plan-targets-container">
                        <div class="data-plan-target-item">
                          <input-list label="Devices" :list="computedDeviceList"
                                      v-model="account.rates.researchAndDevelopment[0].targets.devices"
                                      v-if="!account.rates.researchAndDevelopment[0].allTargets"
                                      :chip-area-max-height="220"
                                      :disabled="researchAndDevelopmentSectionDisabled" />
                        </div>
                      </div>
                      <div class="input-group-checkboxes">
                        <div class="all-devices">
                          <checkbox v-model="account.rates.researchAndDevelopment[0].allTargets" label="All devices"
                                    :disabled="researchAndDevelopmentSectionDisabled" @input="clearAllDeviceLists" />
                        </div>

                        <div class="expires-container">
                          <checkbox label="Expires" v-model="account.rates.researchAndDevelopment[0].cycle.expires"
                                    :disabled="researchAndDevelopmentSectionDisabled" />
                        </div>
                      </div>
                      <div v-if="account.rates.researchAndDevelopment[0].cycle.expires" class="expiration-options">
                        <div class="input-group size-sm-md">
                          <text-input label="Expires on" type="month"
                                      v-model="account.rates.researchAndDevelopment[0].cycle.dueDate" />
                        </div>
                        <div class="input-group">
                          <dropdown v-model="account.rates.researchAndDevelopment[0].cycle.onExpires"
                                    :list="expirationActionsList" :options="{ searchMode: 'none' }" label="On Expiration" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="data-plan-section">
                <div class="data-plan-section-title">In Pause</div>
                <div class="data-plans-list">
                  <div class="data-plan-item last">
                    <div>
                      <div class="data-plan-targets-container">
                        <div class="data-plan-target-item">
                          <input-list label="Devices"
                                      :list="[]"
                                      :value="account.rates.pause[0].targets.devices"
                                      v-if="!account.rates.pause[0].allTargets"
                                      disabled />
                        </div>
                        <div class="input-group-checkboxes">
                          <div class="all-devices">
                            <checkbox v-model="account.rates.pause[0].allTargets" label="All devices"
                                      @input="clearAllDeviceLists" />
                          </div>
                          <div class="expires-container">
                            <checkbox label="Expires" v-model="account.rates.pause[0].cycle.expires" />
                          </div>
                        </div>
                        <div v-if="account.rates.pause[0].cycle.expires" class="expiration-options">
                          <div class="input-group size-sm-md">
                            <text-input label="Expires on" type="month" v-model="account.rates.pause[0].cycle.dueDate" />
                          </div>
                          <div class="input-group">
                            <dropdown v-model="account.rates.pause[0].cycle.onExpires" :list="expirationActionsList"
                                      :options="{ searchMode: 'none' }" label="On Expiration" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="data-plan-section">
                <div class="data-plan-section-title">Marketing Devices</div>
                <div class="data-plans-list">
                  <div class="data-plan-item last">
                    <div>
                      <div class="data-plan-targets-container">
                        <div class="data-plan-target-item">
                          <input-list label="Devices" :list="deviceList" v-model="account.rates.marketingDevices[0].targets.devices"
                                      v-if="!account.rates.marketingDevices[0].allTargets"
                                      :chip-area-max-height="220" />
                        </div>
                        <div class="input-group-checkboxes">
                          <div class="all-devices">
                            <checkbox v-model="account.rates.marketingDevices[0].allTargets" label="All devices"
                                      @input="clearAllDeviceLists" />
                          </div>
                          <div class="expires-container">
                            <checkbox label="Expires" v-model="account.rates.marketingDevices[0].cycle.expires" />
                          </div>
                        </div>
                        <div v-if="account.rates.marketingDevices[0].cycle.expires" class="expiration-options">
                          <div class="input-group size-sm-md">
                            <text-input label="Expires on" type="month" v-model="account.rates.marketingDevices[0].cycle.dueDate" />
                          </div>
                          <div class="input-group">
                            <dropdown v-model="account.rates.marketingDevices[0].cycle.onExpires" :list="expirationActionsList"
                                      :options="{ searchMode: 'none' }" label="On Expiration" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <info-box type="success" :text="infoBoxText" />

          <div v-if="showSaveBtn" class="input-group bottom float-save">
            <form-button @click="save">Save</form-button>
          </div>
        </div>
      </div>

      <modal :title="pauseModalTitle" ref="pauseModal">
        <div class="pause-modal-container">
          <picklist
            v-if="pauseModalActive"
            :key="pauseModalKey"
            :list="pauseModalList"
            :height="300"
            :origin-panel-title="pauseModalOriginTitle"
            :destination-panel-title="pauseModalDestinationTitle"
            :confirm-bulk-moves="false"
            v-model="pauseModalSelection"
          />
        </div>
      </modal>

      <form-button class="scroll-toggle-btn" @click="scrollToPageEdge">
        <i :class="isAtPageBottom ? 'fas fa-arrow-to-top' : 'fas fa-arrow-to-bottom'"></i>
      </form-button>
    </template>
  </page>
</template>

<script>
import Page from '@/components/Page.vue'
import FormButton from '@/components/FormButton.vue'
import TextInput from '@/components/TextInput.vue'
import InfoBox from '@/components/InfoBox.vue'
import Dropdown from '@/components/Dropdown.vue'
import InputList from '@/components/InputList.vue'
import Checkbox from '@/components/Checkbox.vue'
import Modal from '@/components/Modal.vue'
import Picklist from '@/components/Picklist.vue'

import draggable from 'vuedraggable'

import gql from 'graphql-tag'

import {
  syncWorkingAccount,
  goToBillingAccount,
  goToBillingReport,
  platformAccountId,
  isAllAccountsSelected
} from '@/lib/billing-navigation'

export default {
  name: 'BillingAccountsEditorView',

  components: {
    Page,
    FormButton,
    TextInput,
    InfoBox,
    Dropdown,
    InputList,
    Checkbox,
    Modal,
    Picklist,
    draggable
  },

  methods: {
    goToReport () {
      goToBillingReport(this.$router, this.$store, this.$apollo)
    },

    partNumberOptionFromValue (value) {
      if (!value) {
        return { label: '', value: null }
      }
      const str = typeof value === 'object' ? value.value : value
      if (!str) {
        return { label: '', value: null }
      }
      return this.partNumberList.find(i => i.value === str) ?? { label: String(str), value: str }
    },

    getAccount () {
      return this.$apollo.query({
        query: gql`
          query ($id: ID!) {
            billing {
              account: getAccount(id: $id) {
                id: _id
                account {
                  id
                  name
                  value: id
                  label: name
                  system {
                    id
                    name
                  }
                }
                currency
                rates {
                  customerSatisfaction {
                    id: _id
                    allTargets
                    cycle {
                      dueDate
                      onExpires
                    }
                    targets {
                      vehicleTypes {
                        value: id
                        label: name
                      }
                      vehicleDefinitions {
                        value: id
                        label: name
                      }
                      devices {
                        value: id
                        label: name
                      }
                    }
                  }
                  researchAndDevelopment {
                    id: _id
                    allTargets
                    cycle {
                      dueDate
                      onExpires
                    }
                    targets {
                      vehicleTypes {
                        value: id
                        label: name
                      }
                      vehicleDefinitions {
                        value: id
                        label: name
                      }
                      devices {
                        value: id
                        label: name
                      }
                    }
                  }
                  pause {
                    id: _id
                    allTargets
                    cycle {
                      dueDate
                      onExpires
                    }
                    targets {
                      vehicleTypes {
                        value: id
                        label: name
                      }
                      vehicleDefinitions {
                        value: id
                        label: name
                      }
                      devices {
                        value: id
                        label: name
                      }
                    }
                  }
                  marketingDevices {
                    id: _id
                    allTargets
                    cycle {
                      dueDate
                      onExpires
                    }
                    targets {
                      vehicleTypes {
                        value: id
                        label: name
                      }
                      vehicleDefinitions {
                        value: id
                        label: name
                      }
                      devices {
                        value: id
                        label: name
                      }
                    }
                  }
                  normal {
                    id: _id
                    allTargets
                    cycle {
                      cycle
                      period
                      partNumber
                      effectDate
                      autoRenew
                      onExpires
                    }
                    description
                    rate
                    targets {
                      vehicleTypes {
                        value: id
                        label: name
                      }
                      vehicleDefinitions {
                        value: id
                        label: name
                      }
                      devices {
                        value: id
                        label: name
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          id: this.$route.params.id
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        const {
          id,
          account,
          currency,
          rates
        } = response.data.billing.account

        this.account = {
          id,
          account,
          currency: this.currencyList.find(i => i.value === currency),
          rates
        }

        this.account.rates.normal = this.account.rates.normal.map(item => {
          return {
            ...item,
            allTargets: false,
            partNumber: this.partNumberOptionFromValue(item.partNumber ?? item.cycle?.partNumber),
            cycle: {
              ...item.cycle,
              cycle: this.billingCycleList.find(i => i.value === item.cycle?.cycle),
              effectDate: item.cycle?.effectDate?.substring(0, 10) ?? null,
              onExpires: this.expirationActionsList.find(i => i.value === item.cycle?.onExpires)
            }
          }
        })

        this.account.rates.customerSatisfaction = this.account.rates.customerSatisfaction.map(item => {
          return {
            ...item,
            allTargets: item.allTargets ?? false,
            cycle: {
              ...item.cycle,
              expires: item.cycle?.dueDate !== null,
              dueDate: item.cycle?.dueDate?.substring(0, 7) ?? null,
              onExpires: this.expirationActionsList.find(i => i.value === item.cycle?.onExpires)
            }
          }
        })

        this.account.rates.researchAndDevelopment = this.account.rates.researchAndDevelopment.map(item => {
          return {
            ...item,
            allTargets: item.allTargets ?? false,
            cycle: {
              ...item.cycle,
              expires: item.cycle?.dueDate !== null,
              dueDate: item.cycle?.dueDate?.substring(0, 7) ?? null,
              onExpires: this.expirationActionsList.find(i => i.value === item.cycle?.onExpires)
            }
          }
        })

        this.account.rates.pause = this.account.rates.pause.map(item => {
          return {
            ...item,
            allTargets: item.allTargets ?? false,
            cycle: {
              ...item.cycle,
              expires: item.cycle?.dueDate !== null,
              dueDate: item.cycle?.dueDate?.substring(0, 7) ?? null,
              onExpires: this.expirationActionsList.find(i => i.value === item.cycle?.onExpires)
            }
          }
        })

        this.account.rates.marketingDevices = (this.account.rates.marketingDevices ?? []).map(item => {
          return {
            ...item,
            allTargets: item.allTargets ?? false,
            cycle: {
              ...item.cycle,
              expires: item.cycle?.dueDate !== null,
              dueDate: item.cycle?.dueDate?.substring(0, 7) ?? null,
              onExpires: this.expirationActionsList.find(i => i.value === item.cycle?.onExpires)
            }
          }
        })

        if (!this.account.rates.marketingDevices.length) {
          this.account.rates.marketingDevices = [
            {
              id: Math.random(),
              allTargets: false,
              cycle: {
                expires: false,
                dueDate: null,
                onExpires: this.expirationActionsList[0]
              },
              targets: {
                devices: []
              }
            }
          ]
        }

        this.loading = false

        if (account?.id) {
          syncWorkingAccount(this.$store, {
            value: account.id,
            legacyAccountId: account.id,
            label: account.label || account.name,
            billingAccountId: id,
            system: account.system
          })
        }

        this.$moveFocusToFirstElement()
      })
    },

    createAccount () {
      this.$apollo.mutate({
        mutation: gql`
          mutation ($data: BillingAccountInput!) {
            billing {
              createAccount(data: $data) {
                _id
              }
            }
          }
        `,
        variables: {
          data: this.normalizedAccount
        }
      }).then(() => {
        this.$router.push('/billing/accounts')
      }).catch(error => {
        this.infoBoxText = `::error ${error.message}`
      })
    },

    updateAccount (e) {
      this.$apollo.mutate({
        mutation: gql`
          mutation ($id: ID!, $data: BillingAccountInput!) {
            billing {
              updateAccount(id: $id, data: $data) {
                _id
              }
            }
          }
        `,
        variables: {
          id: this.account.id,
          data: this.normalizedAccount
        }
      }).then(() => {
        if (e.shiftKey) {
          this.infoBoxText = '::success Sucessfully saved!'
        } else {
          this.$router.push('/billing/accounts')
        }
      }).catch(error => {
        this.infoBoxText = `::error ${error.message}`
      })
    },

    confirmDeletion () {
      this.$confirm.require({
        message: 'Are you sure you want to delete this billing account?',
        header: 'Delete',
        icon: 'fas fa-exclamation-triangle',

        accept: () => {
          this.deleteAccount()
        }
      })
    },

    deleteAccount () {
      this.$apollo.mutate({
        mutation: gql`
          mutation ($ids: [ID!]!) {
            billing {
              deleteAccounts(ids: $ids)
            }
          }
        `,
        variables: {
          ids: [this.account.id]
        }
      }).then(() => {
        this.$router.push('/billing/accounts')
      }).catch(error => {
        this.infoBoxText = `::error ${error.message}`
      })
    },

    save (e) {
      if (this.isEditing) {
        this.updateAccount(e)
      } else {
        this.createAccount(e)
      }
    },

    searchAccount (searchText) {
      if (searchText.length < 3) {
        this.loadingAccountList = false
        this.accountList = []

        return
      }

      this.loadingAccountList = true

      this.$apollo.query({
        query: gql`
          query ($searchText: String!) {
            platform {
              accounts: searchAccount (searchText: $searchText) {
                id,
                name,
                system {
                  id
                  name
                }
                value: id
                label: name
              }
            }
          }
        `,
        variables: {
          searchText
        }
      }).then(response => {
        this.accountList = response.data.platform.accounts
      }).finally(() => {
        this.loadingAccountList = false
      })
    },

    addBillableRate () {
      this.account.rates.normal.push({
        id: Math.random(),
        allTargets: false,
        partNumber: { label: '', value: null },
        cycle: {
          cycle: {
            value: '',
            label: ''
          },
          period: 12,
          effectDate: '2020-01-01',
          onExpires: {
            value: 'remove',
            label: 'Remove from this data plan'
          }
        },
        description: '',
        rate: 0,
        targets: {
          vehicleTypes: [],
          vehicleDefinitions: [],
          devices: []
        }
      })
    },

    removeBillableRate (id) {
      this.account.rates.normal = this.account.rates.normal.filter(i => i.id !== id)
    },

    toggleRateSortMode () {
      this.rateSortMode = !this.rateSortMode
      if (!this.rateSortMode) {
        this.expandedRateIds = []
      }
    },

    toggleRateExpansion (id) {
      const idx = this.expandedRateIds.indexOf(id)
      if (idx >= 0) {
        this.expandedRateIds.splice(idx, 1)
      } else {
        this.expandedRateIds.push(id)
      }
    },

    isRateIndividuallyExpanded (id) {
      return this.expandedRateIds.includes(id)
    },

    isRateCollapsed (id) {
      return this.rateSortMode && !this.expandedRateIds.includes(id)
    },

    scrollToPageEdge () {
      this.$el.scrollTo({ top: this.isAtPageBottom ? 0 : this.$el.scrollHeight, behavior: 'smooth' })
    },

    updateScrollButton () {
      this.isAtPageBottom = this.$el.scrollTop + this.$el.clientHeight >= this.$el.scrollHeight - 10
    },

    openPauseModal (rate) {
      this.pauseModalMode = 'bill'
      this.currentBillForPause = rate
      this.$refs.pauseModal.open()
    },

    openAllPausedDevicesModal () {
      this.pauseModalMode = 'all'
      this.currentBillForPause = null
      this.pauseModalPausedDevices = this.uniqueDeviceList(this.account.rates.pause[0]?.targets?.devices ?? [])
      this.$refs.pauseModal.open()
    },

    uniqueDeviceList (devices) {
      const uniqueDevices = new Map()

      ;(devices ?? []).forEach(device => {
        if (device?.value == null) return
        uniqueDevices.set(device.value, device)
      })

      return Array.from(uniqueDevices.values())
    },

    clearAllDeviceLists () {
      this.account.rates.normal.forEach(item => {
        item.targets.devices = []
      })

      this.account.rates.customerSatisfaction[0].targets.devices = []
      this.account.rates.researchAndDevelopment[0].targets.devices = []
      this.account.rates.pause[0].targets.devices = []
      this.account.rates.marketingDevices[0].targets.devices = []
    },

    togglePageSearch () {
      this.pageSearchOpen = !this.pageSearchOpen
      if (this.pageSearchOpen) {
        this.$nextTick(() => {
          const ti = this.$refs.pageSearchInput
          if (ti) ti.focus()
        })
      } else {
        this.closePageSearch()
      }
    },

    closePageSearch () {
      clearTimeout(this.pageSearchDebounceId)
      const root = this.$refs.billingPageSearchRoot
      if (root) {
        root.querySelectorAll('.billing-page-search-match').forEach(el => {
          el.classList.remove('billing-page-search-match', 'billing-page-search-match--current')
        })
      }
      this.pageSearchOpen = false
      this.pageSearchQuery = ''
      this.pageSearchMatchIndex = 0
      this.pageSearchMatchCount = 0
      this.pageSearchDebounceId = null
      this._pageSearchHosts = []
    },

    onPageSearchKeydown (e) {
      if (e.key !== 'Enter') return
      e.preventDefault()
      this.pageSearchStep(e.shiftKey ? -1 : 1)
    },

    queuePageSearch () {
      clearTimeout(this.pageSearchDebounceId)
      this.pageSearchDebounceId = setTimeout(() => this.runPageSearch(), 200)
    },

    runPageSearch () {
      const root = this.$refs.billingPageSearchRoot
      const q = this.pageSearchQuery.trim().toLowerCase()
      if (root) {
        root.querySelectorAll('.billing-page-search-match').forEach(el => {
          el.classList.remove('billing-page-search-match', 'billing-page-search-match--current')
        })
      }
      if (!root || !q) {
        this._pageSearchHosts = []
        this.pageSearchMatchCount = 0
        this.pageSearchMatchIndex = 0
        return
      }

      const CHIP_SELECTOR = '.input-list-holder .item'
      const HOST_SELECTOR = '.input-group, .data-plan-section-title, .data-plan-targets-title, .info-box, .input-group-checkboxes'
      const isVisible = (el) => {
        for (let n = el; n && n !== root; n = n.parentElement) {
          const s = window.getComputedStyle(n)
          if (s.display === 'none' || s.visibility === 'hidden') return false
        }
        return true
      }
      const textOf = (el) => {
        const t = el.tagName
        const parts = t === 'INPUT' || t === 'TEXTAREA'
          ? [el.value, el.placeholder]
          : t === 'SELECT'
            ? [el.value, ...Array.from(el.options, o => o.text)]
            : []
        parts.push(el.textContent || '')
        return parts.join(' ').toLowerCase()
      }

      const matches = Array.from(root.querySelectorAll('*')).filter(el => {
        const t = el.tagName
        if (t === 'SCRIPT' || t === 'STYLE' || t === 'NOSCRIPT') return false
        if (t === 'INPUT' && el.type === 'hidden') return false
        return isVisible(el) && textOf(el).includes(q)
      })
      const deepest = matches.filter(el => !matches.some(o => o !== el && el.contains(o)))
      const hosts = Array.from(new Set(deepest.map(el => el.closest(CHIP_SELECTOR) || el.closest(HOST_SELECTOR) || el)))
        .sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)

      this._pageSearchHosts = hosts
      this.pageSearchMatchCount = hosts.length
      if (this.pageSearchMatchIndex >= hosts.length) this.pageSearchMatchIndex = 0
      hosts.forEach((el, i) => {
        el.classList.add('billing-page-search-match')
        if (i === this.pageSearchMatchIndex) el.classList.add('billing-page-search-match--current')
      })
      this.focusPageSearchMatch(hosts[this.pageSearchMatchIndex])
    },

    expandBillForPageSearch (el) {
      const billEl = el && el.closest('.data-plan-item')
      if (!billEl || !this.rateSortMode) return
      const rateId = billEl.getAttribute('data-rate-id')
      if (rateId == null) return
      const rate = this.account.rates.normal.find(r => String(r.id) === rateId)
      if (rate && this.isRateCollapsed(rate.id) && !this.expandedRateIds.includes(rate.id)) {
        this.expandedRateIds.push(rate.id)
      }
    },

    focusPageSearchMatch (el) {
      if (!el) return
      this.expandBillForPageSearch(el)
      this.$nextTick(() => {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
    },

    pageSearchStep (delta) {
      const hosts = this._pageSearchHosts
      if (!hosts || !hosts.length) return
      this.pageSearchMatchIndex = (this.pageSearchMatchIndex + delta + hosts.length) % hosts.length
      hosts.forEach((el, i) => {
        el.classList.toggle('billing-page-search-match--current', i === this.pageSearchMatchIndex)
      })
      this.focusPageSearchMatch(hosts[this.pageSearchMatchIndex])
    }
  },

  computed: {
    isEditing () {
      return !!this.$route.params.id
    },

    pageTitle () {
      return this.isEditing ? 'Edit Billing Account' : 'Create Billing Account'
    },

    showSaveBtn () {
      if (this.isEditing) {
        return this.$checkPermission('billing.accounts.update')
      } else {
        return this.$checkPermission('billing.accounts.create')
      }
    },

    computedVehicleBatchSettings () {
      const selected = []

      Object.values(this.account.rates ?? []).forEach(rateType => {
        if (Array.isArray(rateType)) {
          rateType.forEach(rate => {
            (rate.targets?.vehicleTypes ?? []).forEach(target => {
              selected.push(target.value)
            })
          })
        }
      })

      return this.vehicleBatchSettings.filter(i => !selected.includes(i.value))
    },

    computedVehicleGraphicList () {
      const selected = []

      Object.values(this.account.rates ?? []).forEach(rateType => {
        if (Array.isArray(rateType)) {
          rateType.forEach(rate => {
            (rate.targets?.vehicleDefinitions ?? []).forEach(target => {
              selected.push(target.value)
            })
          })
        }
      })

      return this.vehicleGraphicList.filter(i => !selected.includes(i.value))
    },

    computedDeviceList () {
      const selected = []

      Object.values(this.account.rates ?? []).forEach(rateType => {
        if (Array.isArray(rateType)) {
          rateType.forEach(rate => {
            (rate.targets?.devices ?? []).forEach(target => {
              selected.push(target.value)
            })
          })
        }
      })

      return this.deviceList.filter(i => !selected.includes(i.value))
    },

    pauseModalActive () {
      return this.pauseModalMode === 'all' || this.currentBillForPause
    },

    pauseModalKey () {
      return this.pauseModalMode === 'all'
        ? 'all-paused-devices'
        : this.currentBillForPause?.id
    },

    pauseModalList () {
      if (this.pauseModalMode === 'all') {
        return this.pauseModalPausedDevices
      }

      return this.currentBillForPause?.targets?.devices ?? []
    },

    pauseModalSelection: {
      get () {
        if (this.pauseModalMode === 'all') {
          return this.account.rates.pause[0]?.targets?.devices ?? []
        }

        const billIds = new Set((this.currentBillForPause?.targets?.devices ?? []).map(d => d.value))
        return (this.account.rates.pause[0]?.targets?.devices ?? []).filter(d => billIds.has(d.value))
      },
      set (newSelection) {
        if (this.pauseModalMode === 'all') {
          this.account.rates.pause[0].targets.devices = newSelection
          return
        }

        const billIds = new Set((this.currentBillForPause?.targets?.devices ?? []).map(d => d.value))
        const others = (this.account.rates.pause[0]?.targets?.devices ?? []).filter(d => !billIds.has(d.value))
        this.account.rates.pause[0].targets.devices = [...others, ...newSelection]
      }
    },

    pausedDeviceIds () {
      return (this.account.rates.pause[0]?.targets?.devices ?? []).map(d => d.value)
    },

    pauseModalOriginTitle () {
      return this.pauseModalMode === 'all' ? 'Billable Devices' : 'Billable'
    },

    pauseModalDestinationTitle () {
      return this.pauseModalMode === 'all' ? 'Paused Devices' : 'Paused'
    },

    pauseModalTitle () {
      if (this.pauseModalMode === 'all') {
        return 'Manage Paused Devices'
      }

      const desc = this.currentBillForPause?.description?.trim()
      return desc ? `Pause Devices — ${desc}` : 'Pause Devices'
    },

    normalizedAccount () {
      const customerSatisfaction = this.account.rates.customerSatisfaction[0]
      const researchAndDevelopment = this.account.rates.researchAndDevelopment[0]
      const pause = this.account.rates.pause[0]
      const marketingDevices = this.account.rates.marketingDevices[0] ?? {
        allTargets: false,
        cycle: {},
        targets: {
          devices: []
        }
      }

      return {
        account: {
          id: this.account.account.id,
          name: this.account.account.name,
          system: {
            id: this.account.account.system.id,
            name: this.account.account.system.name
          }
        },
        currency: this.account.currency.value,
        rates: {
          customerSatisfaction: [
            {
              allTargets: customerSatisfaction.allTargets ?? false,
              cycle: {
                dueDate: customerSatisfaction.cycle?.expires
                  ? customerSatisfaction.cycle?.dueDate
                  : undefined,
                onExpires: customerSatisfaction.cycle?.expires
                  ? customerSatisfaction.cycle?.onExpires?.value
                  : undefined
              },
              targets: {
                devices: customerSatisfaction.targets.devices
                  .map(({ value: id, label: name }) => ({ id, name }))
              }
            }
          ],
          researchAndDevelopment: [
            {
              allTargets: researchAndDevelopment.allTargets ?? false,
              cycle: {
                dueDate: researchAndDevelopment.cycle?.expires
                  ? researchAndDevelopment.cycle?.dueDate
                  : undefined,
                onExpires: researchAndDevelopment.cycle?.expires
                  ? researchAndDevelopment.cycle?.onExpires?.value
                  : undefined
              },
              targets: {
                devices: researchAndDevelopment.targets.devices
                  .map(({ value: id, label: name }) => ({ id, name }))
              }
            }
          ],
          pause: [
            {
              allTargets: this.account.rates.pause[0].allTargets ?? false,
              cycle: {
                dueDate: pause.cycle?.expires ? pause.cycle?.dueDate : undefined,
                onExpires: pause.cycle?.expires ? pause.cycle?.onExpires?.value : undefined
              },
              targets: {
                devices: this.account.rates.pause[0].targets.devices
                  .map(({ value: id, label: name }) => ({ id, name }))
              }
            }
          ],
          marketingDevices: [
            {
              allTargets: marketingDevices.allTargets ?? false,
              cycle: {
                dueDate: marketingDevices.cycle?.expires ? marketingDevices.cycle?.dueDate : undefined,
                onExpires: marketingDevices.cycle?.expires ? marketingDevices.cycle?.onExpires?.value : undefined
              },
              targets: {
                devices: (marketingDevices.targets?.devices ?? [])
                  .map(({ value: id, label: name }) => ({ id, name }))
              }
            }
          ],

          normal: this.account.rates.normal.map(item => {
            const targets = {}

            Object.entries(item.targets ?? {}).forEach(([key, value]) => {
              if (Array.isArray(value) && value.length > 0) {
                targets[key] = value.map(({ value: id, label: name }) => ({ id, name }))
              }
            })

            const common = {
              rate: item.rate,
              cycle: {
                cycle: item.cycle.cycle.value,
                period: item.cycle.period,
                partNumber: item.partNumber?.value || undefined,
                effectDate: item.cycle.effectDate,
                autoRenew: item.cycle.autoRenew ?? false,
                onExpires: !item.cycle.autoRenew
                  ? item.cycle.onExpires?.value
                  : undefined
              }
            }

            if (item.cycle.cycle.value === 'monthly') {
              delete common.cycle.period
              delete common.cycle.effectDate
              delete common.cycle.autoRenew
              delete common.cycle.onExpires
            }

            return {
              ...common,
              allTargets: false,
              description: item.description,
              targets
            }
          })
        }
      }
    },

    billableSectionDisabled () {
      const { customerSatisfaction, researchAndDevelopment, pause, marketingDevices } = this.account.rates
      return customerSatisfaction[0]?.allTargets || researchAndDevelopment[0]?.allTargets || pause[0]?.allTargets || marketingDevices[0]?.allTargets
    },

    customerSatisfactionSectionDisabled () {
      const { researchAndDevelopment, pause, marketingDevices } = this.account.rates
      return researchAndDevelopment[0]?.allTargets || pause[0]?.allTargets || marketingDevices[0]?.allTargets
    },

    customerSatisfactionInputsDisabled () {
      return this.customerSatisfactionSectionDisabled
    },

    researchAndDevelopmentSectionDisabled () {
      const { customerSatisfaction, pause, marketingDevices } = this.account.rates
      return customerSatisfaction[0]?.allTargets || pause[0]?.allTargets || marketingDevices[0]?.allTargets
    },

    systemName () {
      return this.account?.account?.system.name ?? ''
    }
  },

  watch: {
    'account.rates.normal': {
      deep: true,
      handler () {
        const billDeviceIds = new Set()
        ;(this.account.rates?.normal ?? []).forEach(rate => {
          (rate.targets?.devices ?? []).forEach(d => billDeviceIds.add(d.value))
        })

        const pauseDevices = this.account.rates?.pause?.[0]?.targets?.devices ?? []
        const filtered = pauseDevices.filter(d => billDeviceIds.has(d.value))
        if (filtered.length !== pauseDevices.length) {
          this.account.rates.pause[0].targets.devices = filtered
        }
      }
    },

    pageSearchQuery () {
      this.pageSearchMatchIndex = 0
      this.queuePageSearch()
    },

    loading (v) {
      if (!v) this.$nextTick(() => this.queuePageSearch())
    },

    'account.account' (platformAccount) {
      if (platformAccount?.id) {
        syncWorkingAccount(this.$store, {
          value: platformAccount.id,
          legacyAccountId: platformAccount.id,
          label: platformAccount.label || platformAccount.name,
          billingAccountId: this.account.id,
          system: platformAccount.system
        })
      }

      this.$apollo.query({
        query: gql`
          query ($accountId: Int!, $systemId: ID!) {
            platform {
              account: getAccount(accountId: $accountId, systemId: $systemId) {
                batchSettings {
                  value: id
                  label: description
                }
                devices {
                  value: id
                  label: name
                }
              }

              system: getSystem(systemId: $systemId) {
                vehicleLayouts {
                  value: id
                  label: description
                }
              }
            }
          }
        `,
        variables: {
          accountId: this.account.account.id,
          systemId: this.account.account.system.id
        }
      }).then(response => {
        this.vehicleBatchSettings = response.data.platform.account.batchSettings
        this.vehicleGraphicList = response.data.platform.system.vehicleLayouts
        this.deviceList = response.data.platform.account.devices
      })
    },

    '$store.state.workingAccount': {
      deep: true,
      handler (account) {
        if (!this.isEditing || this.loading) return

        if (isAllAccountsSelected(account)) {
          this.$router.push('/billing/accounts')
          return
        }

        const selectedPlatformId = platformAccountId(account)

        const currentPlatformId = this.account?.account?.id
        if (currentPlatformId != null && String(currentPlatformId) === String(selectedPlatformId)) {
          return
        }

        goToBillingAccount(this.$router, this.$store, this.$apollo)
      }
    },

    '$route.params.id' (newId, oldId) {
      if (!this.isEditing || !newId || newId === oldId) return

      this.loading = true
      this.getAccount().then(() => {
        if (this.$route.query.managePausedDevices === 'true') {
          this.openAllPausedDevicesModal()
        }
      })
    }
  },

  data () {
    return {
      account: {
        account: null,
        currency: {
          value: 'USD',
          label: 'USD — US Dollar'
        },
        rates: {
          normal: [],
          customerSatisfaction: [
            {
              id: Math.random(),
              allTargets: false,
              cycle: {
                expires: false,
                dueDate: null,
                onExpires: {
                  value: 'remove',
                  label: 'Remove from this data plan'
                }
              },
              targets: {
                devices: []
              }
            }
          ],
          researchAndDevelopment: [
            {
              id: Math.random(),
              allTargets: false,
              cycle: {
                expires: false,
                dueDate: null,
                onExpires: {
                  value: 'remove',
                  label: 'Remove from this data plan'
                }
              },
              targets: {
                devices: []
              }
            }
          ],
          pause: [
            {
              id: Math.random(),
              allTargets: false,
              cycle: {
                expires: false,
                dueDate: null,
                onExpires: {
                  value: 'remove',
                  label: 'Remove from this data plan'
                }
              },
              targets: {
                devices: []
              }
            }
          ],
          marketingDevices: [
            {
              id: Math.random(),
              allTargets: false,
              cycle: {
                expires: false,
                dueDate: null,
                onExpires: {
                  value: 'remove',
                  label: 'Remove from this data plan'
                }
              },
              targets: {
                devices: []
              }
            }
          ]

        }
      },

      loadingAccountList: false,
      accountList: [],
      currencyList: [
        {
          value: 'USD',
          label: 'USD — US Dollar'
        },
        {
          value: 'EUR',
          label: 'EUR — Euro'
        },
        {
          value: 'AUD',
          label: 'AUD — Autralian Dollar'
        },
        {
          value: 'CAD',
          label: 'CAD — Canadian Dollar'
        },
        {
          value: 'ILS',
          label: 'ILS — Israeli New Shekel'
        }
      ],
      billingCycleList: [
        {
          value: 'monthly',
          label: 'Monthly'
        },
        {
          value: 'prepaid',
          label: 'Prepaid'
        }
      ],
      partNumberList: [
        {
          value: '70403-01',
          label: '70403-01 - Data-Monthly'
        },
        {
          value: '70403-42',
          label: '70403-42 - API-Monthly'
        },
        {
          value: '70403-06',
          label: '70403-06 - Prepaid 1 year'
        },
        {
          value: '70403-18',
          label: '70403-18 - Prepaid 5 years'
        },
        {
          value: '70403-19',
          label: '70403-19 - Prepaid 10 years'
        }
      ],
      expirationActionsList: [
        {
          value: 'remove',
          label: 'Remove from this data plan'
        },
        {
          value: 'disable',
          label: 'Disable devices'
        }
      ],
      vehicleBatchSettings: [],
      vehicleGraphicList: [],
      deviceList: [],

      rateSortMode: false,
      expandedRateIds: [],

      currentBillForPause: null,
      pauseModalMode: 'bill',
      pauseModalPausedDevices: [],
      isAtPageBottom: false,

      infoBoxText: null,
      loading: true,

      pageSearchOpen: false,
      pageSearchQuery: '',
      pageSearchMatchIndex: 0,
      pageSearchMatchCount: 0,
      pageSearchDebounceId: null
    }
  },

  mounted () {
    if (this.isEditing) {
      this.getAccount().then(() => {
        if (this.$route.query.managePausedDevices === 'true') {
          this.openAllPausedDevicesModal()
        }
      })
    } else {
      this.loading = false
      this.$moveFocusToFirstElement()
    }
    this.$el.addEventListener('scroll', this.updateScrollButton, { passive: true })
    this.$nextTick(this.updateScrollButton)
    this._onPageSearchEsc = (e) => {
      if (e.key === 'Escape' && this.pageSearchOpen) this.closePageSearch()
    }
    document.addEventListener('keydown', this._onPageSearchEsc)
  },

  beforeDestroy () {
    this.$el.removeEventListener('scroll', this.updateScrollButton)
    document.removeEventListener('keydown', this._onPageSearchEsc)
    clearTimeout(this.pageSearchDebounceId)
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/shared.less';

.billing-accounts-editor-controls {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 8px;
}

.billing-page-search-controls {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
}

/deep/ .billing-page-search-toggle i,
/deep/ .billing-page-search-nav i,
/deep/ .billing-page-search-close i {
  display: inline-block !important;
}

.billing-page-search-toggle {
  border: none;
  background: transparent;
  color: @accent-color;
  cursor: pointer;
  padding: 8px 10px;
  font-size: 16pt;
  line-height: 1;
}

.billing-page-search-expand {
  display: flex;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateX(8px);
  pointer-events: none;
  transition: max-width 0.32s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.24s ease,
              transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-open {
    max-width: 360px;
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
}

.billing-page-search-expand-inner {
  flex: 0 0 auto;
  min-width: 0;
}

.billing-page-search-field-wrap {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  min-width: 240px;
  max-width: 320px;
}

.billing-page-search-input {
  flex: 1;
}

.billing-page-search-close {
  border: none;
  background: transparent;
  color: fade(contrast(@main-color), 45%);
  cursor: pointer;
  padding: 6px 8px;
  font-size: 14pt;
  line-height: 1;
  border-radius: 6px;
  transition: color 150ms ease, background-color 150ms ease;

  &:hover {
    color: @accent-color;
    background: fade(@accent-color, 10%);
  }
}

.billing-page-search-meta {
  margin-top: 4px;
  font-size: 11pt;
  opacity: 0.75;
  display: flex;
  align-items: center;
  gap: 6px;
}

.billing-page-search-nav {
  border: none;
  background: fade(@accent-color, 12%);
  color: @accent-color;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}

/deep/ .billing-page-search-match {
  box-shadow: 0 0 0 2px fade(@accent-color, 35%);
  border-radius: 4px;
}

/deep/ .billing-page-search-match--current {
  box-shadow: 0 0 0 2px @accent-color;
}

/deep/ td.billing-page-search-match {
  background-color: fade(@accent-color, 10%);
  box-shadow: inset 0 0 0 2px fade(@accent-color, 45%);
}

/deep/ td.billing-page-search-match--current {
  background-color: fade(@accent-color, 16%);
  box-shadow: inset 0 0 0 2px @accent-color;
}

/deep/ .input-list-holder .item.billing-page-search-match {
  box-shadow: 0 0 0 2px fade(contrast(@accent-color), 55%);
}

/deep/ .input-list-holder .item.billing-page-search-match--current {
  box-shadow: 0 0 0 2px contrast(@accent-color),
              0 0 0 5px fade(contrast(@accent-color), 35%);
  position: relative;
  z-index: 1;
}

.form {
  padding-bottom: 100px;
}

.float-save {
  position: fixed;
  right: 45px;
  bottom: 24px;
  z-index: 200;

  @media @phone {
    right: 16px;
    bottom: 16px;
  }
}

.scroll-toggle-btn {
  position: fixed;
  right: 45px;
  bottom: 120px;
  width: 42px;
  height: 42px;
  opacity: 0.7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 200;
  font-size: 14pt;
  transition: 200ms;

  @media @phone {
    right: 16px;
    bottom: 74px;
  }
}

.label {
  font-family: Rajdhani, sans-serif;
}

.billing-cycle-group {
  border: 1px solid transparent;
  border-radius: 10px;
  transition: all 500ms;

  @media @phone {
    .never-expires-checkbox {
      margin-top: 10px;
    }
  }

  &.active {
    padding: 15px;
    border-color: fade(contrast(@main-color), 5%);
    margin-bottom: 10px;

    .input-group:last-child {
      margin-bottom: 0 !important;
    }

    &.rate-billing-cycle {
      margin-top: 20px;
    }
  }
}

.data-plans-container {
  background: fade(contrast(@main-color), 1%);
  padding: 20px;
  border-radius: 10px;

  .no-items {
    text-align: center;
    margin-bottom: 20px;
    font-size: 15pt;
    opacity: 0.2;
  }

  .input-group-checkboxes {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  .rate {
    max-width: 200px;
  }

  .section-disabled {
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
  }

  .data-plan-section-title {
    margin-top: 20px;
    margin-bottom: 10px;
    font-size: 14pt;
    font-weight: 100;

    .sort-btn {
      background: fade(contrast(@main-color), 5%);
      float: right;
      width: 30px;
      height: 30px;
      border-radius: 20px;
      border: none;
      color: @accent-color;
      cursor: pointer;

      &.active {
        background: @accent-color;
        color: contrast(@accent-color);
      }

      &:hover {
        opacity: 0.8;
      }

      &:active {
        opacity: 0.7;
      }
    }

    &.first {
      margin-top: 0;
    }
  }

  .data-plan-targets-title {
    margin-top: 10px;
    margin-bottom: 5px;
  }

  .devices-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;

    > :first-child {
      flex: 1;
    }

    .edit-classifications-btn {
      flex: 0 0 36px;
      width: 36px;
      height: 36px;
      font-size: 13pt;
      border-radius: 8px;
      background: fade(@accent-color, 15%);
      color: @accent-color;
      border: 1px solid fade(@accent-color, 25%);
      cursor: pointer;
      transition: 200ms;
      margin-bottom: 5px;

      &:hover {
        background: fade(@accent-color, 30%);
      }

      &:active {
        background: fade(@accent-color, 50%);
      }
    }
  }

  .data-plans-list {
    .data-plan-item {
      display: flex;
      margin-bottom: 20px;
      padding-bottom: 20px;
      border-bottom: 1px dotted fade(contrast(@main-color), 10%);
      position: relative;
      max-height: 2000px;
      overflow: visible;
      transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                  margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                  overflow 0s linear 0.4s;

      &:first-child {
        margin-top: 35px;
      }

      &.last {
        border-bottom: none;
      }

      &:focus-within {
        .data-plan-actions:before {
          background-color: shade(@accent-color, 50%);
        }
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 60px;
        background: linear-gradient(to top, @main-color, fade(@main-color, 0));
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.4s ease;
      }

      >div {
        flex: 1;
      }

      .data-plan-actions {
        flex: 0;
        margin-right: 10px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        align-self: stretch;
        position: relative;

        &:before {
          content: '';
          position: absolute;
          background: fade(contrast(@main-color), 10%);
          width: 10px;
          height: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-radius: 10px;
        }
      }

      .collapse-bill-btn {
        background: fade(contrast(@main-color), 5%);
        float: right;
        width: 30px;
        height: 30px;
        border-radius: 20px;
        border: none;
        color: @accent-color;
        cursor: pointer;
        font-size: 12pt;
        transition: opacity 150ms ease;

        &:hover {
          opacity: 0.8;
        }

        &:active {
          opacity: 0.7;
        }
      }

      .expires-container,
      .all-devices {
        margin: 10px 0;
      }

      .expiration-options {
        padding-left: 35px;
      }

      &.is-collapsed {
        max-height: 109px;
        margin-top: 35px;
        overflow: hidden;
        user-select: none;
        transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                    margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                    overflow 0s linear 0s;

        .data-plan-actions {
          &:before {
            visibility: hidden;
          }
        }
      }
    }
  }

  .icon-btn {
    color: @accent-color;
    font-size: 18pt;
    cursor: pointer;
    border: none;
    background: transparent;
    width: 30px;
    height: 30px;
    background-color: @main-color;
    z-index: 1;
    border-radius: 100%;
    padding: 2px;

    &.grip {
      background: transparent;
      color: fade(contrast(@main-color), 50%);
      cursor: ns-resize;

      &:active {
        opacity: 0.7 !important;
        color: inherit
      }
    }

    &:active {
      color: darken(@accent-color, 20%);
    }

    &:disabled {
      pointer-events: none;
      opacity: 0.5;
      cursor: default;
    }
  }

  .data-plan-footer {
    text-align: center;
  }
}

</style>
